import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ProjectService } from 'src/app/core/services/project.service';
import { Group, Load, StackingOptions } from 'src/app/core/types';
import { ColorPipe } from 'src/app/shared/pipes/color.pipe';
import { FormDataType } from '../../add-load.component';
import { Subscription } from 'rxjs';
import { Project } from '../../../../core/objects/project';
import { Message, MessageService } from '../../../../core/services/message.service';

@Component({
    selector: 'gl-load-stacking',
    templateUrl: './load-stacking.component.html',
    styleUrls: ['./load-stacking.component.scss'],
    standalone: false
})
export class LoadStackingComponent implements OnInit, OnDestroy, OnChanges {
  public form = this.generateForm();
  public stack = false;
  public weightOfFloors = false;
  public chooseLoadsToStack = false;
  public specifyPercentageOfSupport = false;
  public stackAlongFloor = false;
  public loadsGroups: Group[] = [];
  public checkboxLoads = {};
  public stackingLoads: number[] = [];
  private initPctSupport: number;
  @Output() public stackingOptions: EventEmitter<StackingOptions> =
    new EventEmitter<StackingOptions>();
  @Input() public isMyLoads = false;
  public isSelectDisabled = false;
  @Input() public savedStackingOptions: StackingOptions;
  private subscription: Subscription;
  @Input() public loadData: FormDataType | Partial<Load>;
  private messageSubscription: Subscription;

  constructor(
    private projectService: ProjectService,
    private colorPipe: ColorPipe,
    private messageService: MessageService,
  ) {
    this.projectService.project.subscribe((project: Project): void => {
      project.workSpaces[this.projectService.getCurrentWorkSpace()]?.cargoSpaces[0]?.groups.forEach(
        (group: Group): void => {
          if (group.loadType !== 1 && group.loadType !== 2) {
            this.loadsGroups.push(group);
          }
        },
      );
      if (project.workSpaces[this.projectService.getCurrentWorkSpace()]?.cargoSpaces[1]) {
        project.workSpaces[this.projectService.getCurrentWorkSpace()].cargoSpaces[1].groups.forEach(
          (group: Group): void => {
            if (group.loadType !== 1 && group.loadType !== 2) {
              this.loadsGroups.push(group);
            }
          },
        );
      }
      project.workSpaces[this.projectService.getCurrentWorkSpace()]?.outsideSpace.groups.forEach(
        (group: Group): void => {
          if (group.loadType !== 1 && group.loadType !== 2) {
            this.loadsGroups.push(group);
          }
        },
      );

      this.form.get('load').setValue(this.loadsGroups?.[0]?.id);
    });

    if (this.isMyLoads) {
      this.isSelectDisabled = true;
    } else {
      this.isSelectDisabled = !(this.loadsGroups.length > 0);
    }
  }

  private applyStackingOptions(): void {
    if (!this.savedStackingOptions) return;
    this.resetForm();
    this.stack = this.savedStackingOptions.stacking;
    if (this.savedStackingOptions?.maxLoadsOnTop) {
      this.form.controls.stackQuantity.setValue(this.savedStackingOptions.maxLoadsOnTop, { emitEvent: false });
    }
    if (this.savedStackingOptions?.maxWeightOnTop) {
      this.weightOfFloors = true;
      this.form.controls.maxWeight.setValue(this.savedStackingOptions.maxWeightOnTop, { emitEvent: false });
    }
    if (this.savedStackingOptions?.boxesBelowAllowed) {
      this.chooseLoadsToStack = true;
      this.savedStackingOptions.boxesBelowAllowed.forEach((id: number): void => {
        this.checkboxLoads[id] = true;
      });
      this.stackingLoads = [...this.savedStackingOptions.boxesBelowAllowed];
    }
    if (this.savedStackingOptions?.pctSupport) {
      this.specifyPercentageOfSupport = true;
      this.initPctSupport = this.savedStackingOptions?.pctSupport;
      this.form.controls.supportPercentage.setValue(this.savedStackingOptions.pctSupport, { emitEvent: false });
    }
    if (this.savedStackingOptions?.alongFloor) {
      this.stackAlongFloor = true;
    }
  }

  public ngOnInit(): void {
    if (this.loadData.loadType === 2) {
      this.messageSubscription = this.messageService
        .getMessages()
        .subscribe((message: Message): void => {
          if (message.name === 'arrangementChange' && (message.data === 1 || message.data === 2)) {
            if (!this.stack) {
              this.onStack();
            }
          } else if (message.name === 'arrangementChange' && message.data === 0) {
            if (this.stack) {
              this.onStack();
            }
          }
        });
    }
    this.applyStackingOptions();
    if (this.loadData.loadType === 2) {
      this.stack =
        this.savedStackingOptions !== undefined &&
        (this.savedStackingOptions.formation === 2 || this.savedStackingOptions.formation === 1);
    }
    if (!this.stack) {
      this.form.get('stackQuantity').disable();
      this.form.get('maxWeight').disable();
    }

    if(!this.chooseLoadsToStack){
      this.form.get('load').disable();
    }

    this.subscription = this.form.valueChanges.subscribe((changes): void => {
      if (
        changes?.supportPercentage !== this.initPctSupport &&
        changes?.supportPercentage !== 100
      ) {
        this.initPctSupport = changes?.supportPercentage;
        this.specifyPercentageOfSupport = true;
      }
      if (
        changes?.maxWeight !== 0 &&
        changes?.maxWeight !== undefined &&
        changes?.maxWeight !== '' &&
        changes?.maxWeight !== null &&
        this.stack
      ) {
        this.setMaxWeightCheckboxIfValid();
      }
      this.onSubmit();
    });
  }

  private setMaxWeightCheckboxIfValid(): void {
    const isMaxWeightValid: boolean =
      this.form.get('maxWeight').valid &&
      this.form.get('maxWeight').value !== undefined &&
      this.form.get('maxWeight').value !== '' &&
      this.form.get('maxWeight').value !== 0;
    if (isMaxWeightValid) {
      this.weightOfFloors = true;
    }
  }

  private setLoadCheckboxIfValid(): void {
    const isLoadValid: boolean = this.form.get('load').valid && this.stackingLoads.length > 0;
    if (isLoadValid) {
      this.chooseLoadsToStack = true;
    }
  }

  public setStackGroup(id: number): void {
    if (this.stackingLoads.find((idLoad: number): boolean => idLoad === id)) {
      this.stackingLoads = this.stackingLoads.filter((idLoad: number): boolean => idLoad !== id);
    } else {
      this.stackingLoads.push(id);
    }
    this.setLoadCheckboxIfValid();
    this.onSubmit();
  }

  public getColor(group: Group): string {
    return this.colorPipe.transform(group.color);
  }

  public onSubmit(): void {
    const formation = this.stack ? 2 : 0;
    this.stackingOptions.emit({
      stacking: this.stack,
      maxLoadsOnTop: this.stack ? (this.form.controls.stackQuantity.value as number) : undefined,
      boxesBelowAllowed: this.chooseLoadsToStack ? this.stackingLoads : undefined,
      maxWeightOnTop: this.weightOfFloors
        ? (this.form.controls.maxWeight.value as number)
        : undefined,
      alongFloor: this.stackAlongFloor,
      pctSupport: this.specifyPercentageOfSupport
        ? +this.form.controls.supportPercentage.value
        : undefined,
      formation,
    });
  }

  public onStack(): void {
    this.stack = !this.stack;
    if (this.stack) {
      this.form.get('stackQuantity').enable();
      this.form.get('maxWeight').enable();
    } else {
      this.form.get('stackQuantity').disable();
      this.form.get('maxWeight').disable();
      this.resetForm();
    }
    this.onSubmit();
    if (this.loadData.loadType === 2) {
      this.messageService.sendMessage({ name: 'stackingChange', data: this.stack });
    }
  }

  private resetForm(): void {
    this.form.controls.stackQuantity.setValue(null, { emitEvent: false });
    this.form.controls.maxWeight.setValue(null, { emitEvent: false });
    this.form.controls.load.setValue(this.loadsGroups?.[0]?.id, { emitEvent: false });
    this.form.get('supportPercentage').setValue(100, { emitEvent: false });
    this.specifyPercentageOfSupport = false;
    this.stack = false;
    this.weightOfFloors = false;
    this.chooseLoadsToStack = false;
    this.stackAlongFloor = false;
  }

  public onChooseLoadsToStack(): void {
    this.chooseLoadsToStack = !this.chooseLoadsToStack;
    if (this.chooseLoadsToStack) {
      this.form.get('load').enable();
    }
    else {
      this.form.get('load').disable();
    }
    this.onSubmit();
  }

  public onSpecifyPercentageOfSupport(): void {
    this.specifyPercentageOfSupport = !this.specifyPercentageOfSupport;
    this.onSubmit();
  }

  public onStackAlongFloor(): void {
    this.stackAlongFloor = !this.stackAlongFloor;
    this.onSubmit();
  }

  public onWeightOfFloors(): void {
    this.weightOfFloors = !this.weightOfFloors;
    this.onSubmit();
  }

  private generateForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      stackQuantity: new UntypedFormControl(),
      maxWeight: new UntypedFormControl(0),
      load: new UntypedFormControl(this.loadsGroups?.[0]?.id),
      supportPercentage: new UntypedFormControl(100),
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.savedStackingOptions && changes.savedStackingOptions.currentValue) {
      this.applyStackingOptions();
    }
  }
}
